import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GetSetDataService } from '../../services/get-set-data.service';

export interface DictionaryTerm {
  term: string;
  definition: string;
  owner: string;
  stakeholders: string;
  sampleData: string;
  code: string;
  system: string;
}

@Component({
  selector: 'app-data-dictionary',
  templateUrl: './data-dictionary.component.html',
  styleUrls: ['./data-dictionary.component.scss'],
  imports: [CommonModule, FormsModule]
})
export class DataDictionaryComponent implements OnInit, OnDestroy {

  static classRemoveBigBar: string = 'helper-remove-big-search';
  static classFullWidth: string = 'helper-full-width-content';

  searchQuery: string = '';

  terms: DictionaryTerm[] = [
    {
      term: 'Student ID',
      definition: 'A unique numeric identifier assigned to each enrolled student at the time of admission. Used as the primary key across all student-related systems.',
      owner: 'Registrar',
      stakeholders: 'Financial Aid, Bursar, Academic Affairs',
      sampleData: '20241234',
      code: 'STU_ID',
      system: 'Banner'
    },
    {
      term: 'Enrolled Credits',
      definition: 'The total number of credit hours a student is registered for in a given term. Used to determine full-time vs part-time status for billing and financial aid.',
      owner: 'Registrar',
      stakeholders: 'Financial Aid, Bursar',
      sampleData: '15',
      code: 'ENROLLED_CREDITS',
      system: 'Banner'
    },
    {
      term: 'GPA (Cumulative)',
      definition: 'The grade point average calculated across all terms a student has attended. Weighted by credit hours per course.',
      owner: 'Academic Affairs',
      stakeholders: 'Registrar, Dean\'s Office, Financial Aid',
      sampleData: '3.45',
      code: 'CUMULATIVE_GPA',
      system: 'Banner'
    },
    {
      term: 'Net Tuition Revenue',
      definition: 'Gross tuition billed minus institutional aid, scholarships, and tuition remission. Represents actual revenue received per student.',
      owner: 'Finance',
      stakeholders: 'CFO, Enrollment Management, Financial Aid',
      sampleData: '$18,500',
      code: 'NET_TUITION_REV',
      system: 'Workday'
    },
    {
      term: 'FTE (Full-Time Equivalent)',
      definition: 'A normalized unit representing one full-time student. Part-time students are counted as fractions (e.g., 9 credits = 0.75 FTE). Used for state reporting and accreditation.',
      owner: 'Institutional Research',
      stakeholders: 'Provost, Finance, Registrar',
      sampleData: '0.75',
      code: 'STUDENT_FTE',
      system: 'Banner'
    },
    {
      term: 'Headcount',
      definition: 'The total count of distinct students enrolled in a given term, regardless of credit load. Differs from FTE in that each student counts as exactly 1.',
      owner: 'Institutional Research',
      stakeholders: 'Provost, Finance, Board of Trustees',
      sampleData: '4,812',
      code: 'TERM_HEADCOUNT',
      system: 'Banner'
    },
    {
      term: 'Employee ID',
      definition: 'A unique identifier assigned to each employee upon hire. Persists through role changes and is used as the primary key in HR and payroll systems.',
      owner: 'Human Resources',
      stakeholders: 'Payroll, IT, Finance',
      sampleData: 'EMP-00891',
      code: 'EMPLOYEE_ID',
      system: 'Workday'
    },
    {
      term: 'Position FTE',
      definition: 'The budgeted full-time equivalent for a given position. A 0.5 FTE position is funded at half the standard full-time salary and benefits.',
      owner: 'Human Resources',
      stakeholders: 'Finance, Department Heads, Provost',
      sampleData: '1.0',
      code: 'POSITION_FTE',
      system: 'Workday'
    },
    {
      term: 'Cohort Retention Rate',
      definition: 'The percentage of first-time, full-time degree-seeking students who return to the same institution the following fall term.',
      owner: 'Institutional Research',
      stakeholders: 'Provost, Enrollment Management, Student Affairs',
      sampleData: '84%',
      code: 'COHORT_RETENTION_RATE',
      system: 'Banner'
    },
    {
      term: 'Pell Eligible',
      definition: 'A flag indicating whether a student qualifies for a Federal Pell Grant based on their Expected Family Contribution (EFC) from the FAFSA.',
      owner: 'Financial Aid',
      stakeholders: 'Institutional Research, Finance',
      sampleData: 'Y / N',
      code: 'PELL_ELIGIBLE_FLAG',
      system: 'Banner'
    },
    {
      term: 'Department Cost Center',
      definition: 'A unique budget tracking code assigned to each academic or administrative department for financial reporting and expense allocation.',
      owner: 'Finance',
      stakeholders: 'All Departments, Provost, CFO',
      sampleData: 'CC-4421',
      code: 'DEPT_COST_CENTER',
      system: 'Workday'
    },
    {
      term: 'Degree Conferral Date',
      definition: 'The official date on which a degree was formally awarded to a student, as recorded by the Registrar. Used for alumni reporting and transcript verification.',
      owner: 'Registrar',
      stakeholders: 'Alumni Relations, Institutional Research',
      sampleData: '2024-05-18',
      code: 'DEGREE_CONFERRAL_DT',
      system: 'Banner'
    }
  ];

  get filteredTerms(): DictionaryTerm[] {
    if (!this.searchQuery.trim()) return this.terms;
    const q = this.searchQuery.toLowerCase();
    return this.terms.filter(t =>
      t.term.toLowerCase().includes(q) ||
      t.definition.toLowerCase().includes(q) ||
      t.owner.toLowerCase().includes(q) ||
      t.system.toLowerCase().includes(q) ||
      t.stakeholders.toLowerCase().includes(q)
    );
  }

  systemClass(system: string): string {
    return 'system-tag--' + system.toLowerCase().replace(/\s+/g, '-');
  }

  constructor(private getSet: GetSetDataService) {}

  ngOnInit() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.add(DataDictionaryComponent.classRemoveBigBar);
    body.classList.add(DataDictionaryComponent.classFullWidth);
    this.getSet.scrollToTop();
  }

  ngOnDestroy() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.remove(DataDictionaryComponent.classRemoveBigBar);
    body.classList.remove(DataDictionaryComponent.classFullWidth);
  }
}
